import styles from "./DisruptorBlock.module.scss";
import classNames from "classnames/bind";
import { ESIInclude } from "@packages/esi-include/SimpleESIInclude";
import {
  FeatureFlag,
  FeatureFlags,
  FeatureFlagValue,
} from "@packages/feature-flags";

const cx = classNames.bind(styles);

export type DisruptorBlockDetails = {
  id?: string;
  gridIndex: number;
  maxWidth: number;
  esiPath?: string;
};

export const DisruptorBlock = ({
  indexInGrid,
  productCount,
  flags,
  finalCreditBannerIndex,
  className,
}: {
  indexInGrid: number;
  productCount: number;
  flags: FeatureFlags;
  finalCreditBannerIndex: number;
  className?: string;
}) => {
  const allDisruptorBlockDetails = getDisruptorBlockDetails(
    flags,
    finalCreditBannerIndex,
  );
  const disruptorBlockDetails = allDisruptorBlockDetails
    .filter((details) =>
      shouldDisplayDisruptorBlock(indexInGrid, details, productCount),
    )
    .map((details) => {
      const firstBlock = allDisruptorBlockDetails.find(
        (block) =>
          block.id === details.id || block.gridIndex === details.gridIndex,
      );

      if (firstBlock !== details) {
        // eslint-disable-next-line no-console -- Report invalid banner configuration.
        console.error("Ignoring duplicate disruptor block", {
          ignoredBlock: details,
          firstBlock,
        });
        return null;
      }

      return details;
    })
    .filter((details): details is DisruptorBlockDetails => details !== null);

  if (disruptorBlockDetails.length === 0) {
    return null;
  }

  return (
    <>
      {disruptorBlockDetails.map((details, index) => (
        <div
          key={`${details.id}-${index}`}
          data-component="DisruptorBlock"
          data-disruptor-block={details.id}
          data-testid="disruptor-block"
          className={cx(
            "disruptor-block",
            `disruptor-block--${details.maxWidth}-span`,
            className,
          )}
        >
          {details.esiPath && (
            <ESIInclude
              src={details.esiPath}
              id={
                disruptorBlockDetails.length > 1
                  ? `web-plp-scaffold-ui-disruptor-block-${details.id}`
                  : "web-plp-scaffold-ui-disruptor-block"
              }
            />
          )}
        </div>
      ))}
    </>
  );
};

const getDisruptorBlockDetails = (
  flags: FeatureFlags,
  finalCreditBannerIndex: number,
): DisruptorBlockDetails[] => {
  const idFlag = flags?.disruptorBlockId;
  const monetateIdFlags =
    idFlag?.source === "monetate"
      ? [idFlag, ...(idFlag.additionalExperiences ?? [])]
      : [];
  const experienceIds =
    idFlag?.source === "monetate"
      ? monetateIdFlags
          .map((flag) => flag.experienceId)
          .filter(
            (experienceId): experienceId is number =>
              experienceId !== undefined,
          )
      : [undefined];

  return experienceIds.map((experienceId) => ({
    id: extractStringValue(getFlagByExperienceId(idFlag, experienceId)),
    gridIndex:
      (extractNumberValue(
        getFlagByExperienceId(flags?.disruptorBlockGridIndex, experienceId),
      ) || 11) + finalCreditBannerIndex,
    maxWidth:
      extractNumberValue(
        getFlagByExperienceId(flags?.disruptorBlockMaxWidth, experienceId),
      ) || 3,
    esiPath: extractStringValue(
      getFlagByExperienceId(flags?.disruptorBlockEsiPath, experienceId),
    ),
  }));
};

const getFlagByExperienceId = (
  flag: FeatureFlag | undefined,
  experienceId?: number,
): FeatureFlagValue | undefined => {
  if (!flag) {
    return undefined;
  }

  if (flag.source === "monetate" && experienceId !== undefined) {
    return [flag, ...(flag.additionalExperiences ?? [])].find(
      (experience) => experience.experienceId === experienceId,
    )?.value;
  }

  if (flag.source !== "monetate" && !experienceId) {
    return flag.value;
  }

  return undefined;
};

const shouldDisplayDisruptorBlock = (
  ind: number,
  disruptorBlockDetails: DisruptorBlockDetails,
  productCount: number,
) => {
  if (!disruptorBlockDetails.id) {
    return false;
  }

  // this is the disruptor block index
  if (disruptorBlockDetails?.gridIndex === ind) {
    return true;
  }

  // this is last product & disruptor block index is after last product
  if (
    ind === productCount - 1 &&
    disruptorBlockDetails?.gridIndex >= productCount
  ) {
    return true;
  }

  return false;
};

const extractStringValue = (value: string | boolean | null | undefined) =>
  typeof value === "string" ? value : undefined;

const extractNumberValue = (value: string | boolean | null | undefined) =>
  typeof value === "string" ? parseInt(value, 10) : undefined;
